import { tightJavaScriptify, whenStr } from "@phosphor/utils";
import { Schema, identity } from "effect";
import { atom, useAtom, useSetAtom } from "jotai";
import { selectAtom, useAtomCallback } from "jotai/utils";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Ta } from "#scripts/components/ui/icons.tsx";
import { useWorldStateMaybe } from "#scripts/vmc/WorldState-impl.ts";
import { getUIDColor } from "#scripts/world-state-demo/getUIDColor.tsx";
import { useStorageValue } from "../hooks/useStorageValue.ts";
import { AtomValue } from "../jotai/AtomValue.tsx";
import { isAtom } from "../jotai/isAtom.ts";
import { createJotaiAtomsHook } from "../jotai/jotai-atoms-tight-javascriptify.ts";
import { cn } from "../utils.ts";
import { getColorFor } from "./getColorForString.tsx";
import { useClickByHoverToAvoidChangingFocus } from "./useClickByHoverToAvoidChangingFocus.tsx";

// Custom hook for jittered interval updates
const useJitteredInterval = (fn: () => void, delay: number, deps: any[]) => {
  useEffect(() => {
    const tick = () => {
      const jitter = delay * 0.3 * (Math.random() - 0.5);
      const timer = setTimeout(() => {
        fn();
        tick();
      }, delay + jitter);
      return timer;
    };

    const timer = tick();
    return () => clearTimeout(timer);
  }, [fn, delay, ...deps]);
};

interface DevPrintProps extends Omit<React.ComponentPropsWithoutRef<"pre">, "value"> {
  value: unknown;
  /** Comma separated list of keys to print from the value. */
  keys?: string;
  title?: string;
}

const printSizeAtom = atom(12);
export const DevPrint = React.forwardRef<HTMLPreElement, DevPrintProps>(
  ({ value, keys, title, className, ...props }, ref) => {
    const [titleState, setTitleState] = useStorageValue(`DevPrint(${title})`, localStorage, () => ({
      collapsed: Schema.Boolean,
    }));
    const [countAtom] = useState(() => atom(1));
    const setCount = useSetAtom(countAtom);
    const world = useWorldStateMaybe();
    const logValue = useAtomCallback((get) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      let v = typeof value === "function" ? value(get) : value;
      v = isAtom(v) ? get(v) : v;
      console.log(v);
    });
    const [printSize, setPrintSize] = useAtom(printSizeAtom);
    const increasePrintSize = useCallback(() => setPrintSize((s) => s + 2), [setPrintSize]);
    const decreasePrintSize = useCallback(() => setPrintSize((s) => s - 2), [setPrintSize]);

    const stringifiedAtom = useMemo(() => {
      return selectAtom(
        atom((get) =>
          (
            (get(countAtom) &&
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call
              tightJavaScriptify(typeof value === "function" ? value(get) : value, [
                createJotaiAtomsHook(get),
                keys && {
                  name: "keys",
                  replacer: (key, value) => {
                    const onlyKeys = keys.split(",");
                    if (!key && value && typeof value === "object")
                      return {
                        value: Object.fromEntries(
                          onlyKeys.flatMap((k) => (k in value ? [[k, (value as any)[k]]] : [])),
                        ),
                      };
                    return null;
                  },
                },
              ])) as string
          )
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            // Replace keys with bold color
            .replace(
              /^([{\s]+)([^:["\s]+)/gm,
              (_, prefix, name) => `${prefix}<b style="color: ${getColorFor(name).textPrimary.color}">${name}</b>`,
            )
            // Replace braces at start and end of lines with muted color
            .replace(/(^[\[{}\s]+|[{}[\]\s,]+$)/gm, (_, prefix) => `<span style="opacity: 0.5">${prefix}</span>`)
            .replace(/"([^"|]+\|[^"|]+\|[^"|]+)"/g, (_, uid) => {
              try {
                const entity = world?.getEntityMaybe(uid);
                if (!world || !entity) {
                  return `<em data-loc="phosphor-blog/scripts/lib/dev/DevPrint.tsx:97:31" data-name="em" data-uid="${uid}">${uid}</em>`;
                }

                const color = getUIDColor(uid);
                const style = `background: ${color.container.backgroundColor}; color: ${color.textPrimary.color}; border-radius: 3px; padding: 2px 4px;`;
                return `<b data-loc="phosphor-blog/scripts/lib/dev/DevPrint.tsx:102:28" data-name="b" data-uid="${uid}" style="${style}">${entity._name}</b>`;
              } catch (e) {
                return `<em data-loc="phosphor-blog/scripts/lib/dev/DevPrint.tsx:104:29" data-name="em" data-uid="${uid}">${uid}</em>`;
              }
            }),
        ),
        identity,
      );
    }, [value, countAtom, world, keys]);

    const lengthAtom = useMemo(
      () => atom((get) => whenStr(get(stringifiedAtom), (s) => `len(${s.length})`)),
      [stringifiedAtom],
    );

    // Keep DevPrint updated for non-reactive content like ProseMirror
    useJitteredInterval(() => setCount((a) => a + 1), 2000, [setCount]);

    const [titleElt, setTitleElt] = useState<HTMLDivElement | null>(null);
    const handleClick = useClickByHoverToAvoidChangingFocus(titleElt, () =>
      setTitleState((a) => ({ ...a, collapsed: !a?.collapsed })),
    );

    return (
      <pre
        ref={ref}
        {...props}
        className={cn("relative whitespace-pre-wrap max-w-full py-4 rounded-md bg-gray-50", className)}
        onClick={() => setCount((a) => a + 1)}
        style={{ fontSize: `${printSize}px` }}
      >
        {title && (
          <div
            data-loc="phosphor-blog/scripts/lib/dev/DevPrint.tsx:135:13" data-name="div" className="flex items-center gap-2 text-sm text-gray-500 cursor-pointer hover:text-gray-700 transition-colors mb-2"
            ref={setTitleElt}
            onClick={handleClick}
          >
            <Ta.IconChevronRight data-loc="phosphor-blog/scripts/lib/dev/DevPrint.tsx:139:34" data-name="Ta.IconChevronRight" className={cn("w-4 h-4 transition-transform", !titleState?.collapsed && "rotate-90")} />
            {title} {titleState?.collapsed && <div data-loc="phosphor-blog/scripts/lib/dev/DevPrint.tsx:140:52" data-name="div" className="text-sm text-gray-400 italic">Hidden</div>}
          </div>
        )}

        {!titleState?.collapsed && (
          <>
            <AtomValue atom={stringifiedAtom}>
              {(html) => (
                <div
                  data-loc="phosphor-blog/scripts/lib/dev/DevPrint.tsx:149:19" data-name="div" className="pl-2 whitespace-pre-wrap max-w-full overflow-x-auto overflow-y-hidden leading-tight"
                  // biome-ignore lint/security/noDangerouslySetInnerHtml: Required for rendering formatted content
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              )}
            </AtomValue>
          </>
        )}
        <span data-loc="phosphor-blog/scripts/lib/dev/DevPrint.tsx:157:15" data-name="span" className="absolute top-2 right-4 text-sm text-gray-400 flex gap-2 items-center">
          {!titleState?.collapsed && (
            <>
              <button data-loc="phosphor-blog/scripts/lib/dev/DevPrint.tsx:160:23" data-name="button" className="hover:text-gray-700 transition-colors" onClick={decreasePrintSize} children="-" />
              <button data-loc="phosphor-blog/scripts/lib/dev/DevPrint.tsx:161:23" data-name="button" className="hover:text-gray-700 transition-colors" onClick={increasePrintSize} children="+" />
              <AtomValue atom={lengthAtom} />
            </>
          )}
          <button data-loc="phosphor-blog/scripts/lib/dev/DevPrint.tsx:165:19" data-name="button" className="text-sm text-gray-400 hover:text-gray-700 transition-colors" onClick={logValue}>
            LOG
          </button>
        </span>
      </pre>
    );
  },
);

DevPrint.displayName = "DevPrint";
